import { useEffect } from "react";

/**
 * useDocumentMeta, atualiza dinamicamente <title>, meta description,
 * Open Graph e Twitter cards na <head>. Sem dependências externas.
 *
 * Restaura os valores anteriores no unmount.
 */

const DEFAULTS = {
  title: "Carlos Medeiros, Pregador, Autor e Pastor",
  description:
    "Devocionais diários, sermões, planos de leitura e ferramentas para seu crescimento espiritual.",
  image: "https://cemedeiros.com.br/couple-carlos-milena.jpg",
  url: "https://cemedeiros.com.br/",
  type: "website",
};

function upsertMeta(selector, attr, key, value) {
  if (typeof document === "undefined") return null;
  let el = document.querySelector(selector);
  const prev = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return { el, prev };
}

function upsertLink(rel, href) {
  if (typeof document === "undefined") return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  const prev = el?.getAttribute("href") ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, prev };
}

export default function useDocumentMeta({
  title,
  description,
  image,
  url,
  type,
  canonical,
  jsonLd,
} = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const prevTitle = document.title;
    const t = title || DEFAULTS.title;
    const d = description || DEFAULTS.description;
    const img = image || DEFAULTS.image;
    const u = url || DEFAULTS.url;
    const tp = type || DEFAULTS.type;

    document.title = t;

    const updates = [
      upsertMeta('meta[name="description"]', "name", "description", d),
      upsertMeta('meta[property="og:title"]', "property", "og:title", t),
      upsertMeta('meta[property="og:description"]', "property", "og:description", d),
      upsertMeta('meta[property="og:image"]', "property", "og:image", img),
      upsertMeta('meta[property="og:url"]', "property", "og:url", u),
      upsertMeta('meta[property="og:type"]', "property", "og:type", tp),
      upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", t),
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", d),
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", img),
    ];
    const linkUpdate = canonical ? upsertLink("canonical", canonical) : null;

    // JSON-LD structured data
    let scriptEl = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-managed-by", "useDocumentMeta");
      try {
        scriptEl.text = JSON.stringify(jsonLd);
      } catch {
        scriptEl = null;
      }
      if (scriptEl) document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      updates.forEach((up) => {
        if (!up) return;
        if (up.prev !== null) up.el.setAttribute("content", up.prev);
      });
      if (linkUpdate && linkUpdate.prev !== null) {
        linkUpdate.el.setAttribute("href", linkUpdate.prev);
      }
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [title, description, image, url, type, canonical, JSON.stringify(jsonLd || null)]);
}
